import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Users, Calendar, MessageSquare, ShoppingBag, Award, BookOpen,
  MapPin, Heart, Star, ChevronRight, LogIn, UserPlus, Crown,
  Shield, Globe, Dog, ExternalLink, Settings, Rss, Languages,
  Megaphone, Pin
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Language labels for display
const LANGUAGE_LABELS = {
  en: { name: 'English', flag: '🇺🇸' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  pt: { name: 'Português', flag: '🇵🇹' },
  it: { name: 'Italiano', flag: '🇮🇹' }
};

const ClubHomepage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [joining, setJoining] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [showLangMenu, setShowLangMenu] = useState(false);

  // Get translated content based on current language
  const getTranslatedContent = (field) => {
    if (!club) return '';
    // If viewing in default language, return original
    if (currentLanguage === club.default_language) {
      return club[field] || '';
    }
    // Otherwise check translations
    const translations = club.translations || {};
    if (translations[currentLanguage] && translations[currentLanguage][field]) {
      return translations[currentLanguage][field];
    }
    // Fallback to original
    return club[field] || '';
  };

  useEffect(() => {
    fetchClubData();
  }, [slug]);

  useEffect(() => {
    if (user && club) {
      fetchMembershipStatus();
    }
  }, [user, club]);

  const fetchClubData = async () => {
    setLoading(true);
    try {
      // Fetch club by slug
      const response = await fetch(`${API}/api/clubs/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setClub(data);
        // Set initial language to club's default
        setCurrentLanguage(data.default_language || 'en');
        
        // Fetch recent forum posts
        const postsRes = await fetch(`${API}/api/forums/recent?limit=5`);
        if (postsRes.ok) {
          const postsData = await postsRes.json();
          setRecentPosts(postsData.slice(0, 5));
        }
        
        // Fetch upcoming events
        const eventsRes = await fetch(`${API}/api/events?limit=3`);
        if (eventsRes.ok) {
          const eventsData = await eventsRes.json();
          setUpcomingEvents(eventsData.slice(0, 3));
        }
        
        // Fetch club announcements
        const announcementsRes = await fetch(`${API}/api/announcements/?club_id=${data.id}&limit=5`);
        if (announcementsRes.ok) {
          const announcementsData = await announcementsRes.json();
          setAnnouncements(announcementsData);
        }
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching club:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipStatus = async () => {
    try {
      const response = await fetch(`${API}/api/clubs/my-membership`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembership(data);
      }
    } catch (error) {
      console.error('Error fetching membership:', error);
    }
  };

  const handleJoinClub = async () => {
    if (!user) {
      navigate(`/register?redirect=/club/${slug}`);
      return;
    }

    if (!membership?.is_club_member) {
      navigate('/membership');
      return;
    }

    setJoining(true);
    try {
      const response = await fetch(`${API}/api/clubs/join/${club.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchMembershipStatus();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to join club');
      }
    } catch (error) {
      console.error('Error joining club:', error);
    } finally {
      setJoining(false);
    }
  };

  const isClubMember = membership?.clubs?.some(c => c.id === club?.id);
  const isPaidMember = membership?.member_class && !['free', 'guest'].includes(membership.member_class);
  const showAds = !isPaidMember;
  
  // Check if user is a club officer/admin
  const isClubOfficer = user && (
    user.is_admin || 
    user.is_operator_admin || 
    ['club_officer', 'ngo_officer'].includes(user.member_class) ||
    membership?.clubs?.some(c => c.id === club?.id && ['admin', 'owner', 'officer'].includes(c.role))
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Club Not Found</h2>
          <p className="text-gray-600 mt-2">This club doesn't exist or may have been removed.</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Club Header/Banner */}
      <div 
        className="relative h-64 md:h-80"
        style={{ backgroundColor: club.primary_color }}
      >
        {club.banner_url ? (
          <img 
            src={club.banner_url} 
            alt={getTranslatedContent('name')} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Dog className="w-32 h-32 text-white/20" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        {/* Language Selector - Top Right */}
        {club.supported_languages && club.supported_languages.length > 1 && (
          <div className="absolute top-4 right-4 z-10">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/90 hover:bg-white border-none shadow-lg"
                onClick={() => setShowLangMenu(!showLangMenu)}
                data-testid="language-selector"
              >
                <Languages className="w-4 h-4 mr-2" />
                {LANGUAGE_LABELS[currentLanguage]?.flag} {LANGUAGE_LABELS[currentLanguage]?.name || currentLanguage.toUpperCase()}
              </Button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border py-1 z-20">
                  {club.supported_languages.map(lang => (
                    <button
                      key={lang}
                      onClick={() => {
                        setCurrentLanguage(lang);
                        setShowLangMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                        currentLanguage === lang ? 'bg-gray-50 font-medium' : ''
                      }`}
                      data-testid={`lang-option-${lang}`}
                    >
                      {LANGUAGE_LABELS[lang]?.flag} {LANGUAGE_LABELS[lang]?.name || lang.toUpperCase()}
                      {lang === club.default_language && (
                        <span className="ml-auto text-xs text-gray-400">default</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Club Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-end gap-4">
            {/* Logo */}
            {club.logo_url ? (
              <img 
                src={club.logo_url} 
                alt={club.logo_alt || getTranslatedContent('name')} 
                className="w-20 h-20 md:w-24 md:h-24 rounded-xl shadow-lg bg-white object-contain p-2"
              />
            ) : (
              <div 
                className="w-20 h-20 md:w-24 md:h-24 rounded-xl shadow-lg flex items-center justify-center"
                style={{ backgroundColor: club.secondary_color }}
              >
                <Dog className="w-12 h-12 text-white" />
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {club.club_type === 'flagship' && (
                  <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Official
                  </span>
                )}
                {club.club_type === 'ngo' && (
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                    <Heart className="w-3 h-3" /> NGO/Rescue
                  </span>
                )}
                {/* Country Flag */}
                {club.country && club.country !== 'US' && (
                  <span className="px-2 py-0.5 bg-white/20 text-white text-xs font-medium rounded-full flex items-center gap-1">
                    <Globe className="w-3 h-3" /> {club.country}
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">{getTranslatedContent('name')}</h1>
              <p className="text-white/80 text-lg">{getTranslatedContent('tagline')}</p>
            </div>
            
            {/* Member Count & Join Button */}
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2 text-white/80">
                <Users className="w-5 h-5" />
                <span>{club.member_count || 0} members</span>
              </div>
              
              {!user ? (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    onClick={() => navigate(`/login?redirect=/club/${slug}`)}
                    data-testid="club-login-button"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Login
                  </Button>
                  <Button 
                    className="bg-white text-gray-900 hover:bg-gray-100"
                    onClick={() => navigate(`/register?redirect=/club/${slug}`)}
                    data-testid="club-join-button"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Join Now
                  </Button>
                </div>
              ) : !isClubMember ? (
                <Button 
                  onClick={handleJoinClub}
                  disabled={joining}
                  className="bg-white text-gray-900 hover:bg-gray-100"
                  data-testid="upgrade-join-button"
                >
                  {joining ? (
                    <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  {isPaidMember ? 'Join Club' : 'Upgrade to Join'}
                </Button>
              ) : (
                <span className="px-4 py-2 bg-green-500 text-white rounded-lg flex items-center gap-2" data-testid="member-badge">
                  <Shield className="w-4 h-4" />
                  Member
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Ad Banner for Free Users - Themed to club colors */}
        {showAds && (
          <div 
            className="rounded-xl p-6 mb-8 border"
            style={{ 
              background: `linear-gradient(135deg, ${club.primary_color}10, ${club.secondary_color}15)`,
              borderColor: `${club.primary_color}30`
            }}
          >
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {!user 
                    ? (currentLanguage === 'es' ? `¡Únete a ${getTranslatedContent('name')} Hoy!` : `Join ${getTranslatedContent('name')} Today!`)
                    : (currentLanguage === 'es' ? 'Mejora tu Membresía' : 'Upgrade Your Membership')}
                </h3>
                <p className="text-gray-600 mt-1">
                  {!user 
                    ? (currentLanguage === 'es' 
                        ? 'Crea una cuenta gratuita para guardar favoritos e interactuar con la comunidad.'
                        : 'Create a free account to save favorites and interact with the community.')
                    : (currentLanguage === 'es'
                        ? 'Conviértete en miembro de pago para eliminar anuncios, acceder a recursos exclusivos y unirte a clubes.'
                        : 'Become a full member to remove ads, access exclusive resources, and join clubs.')}
                </p>
              </div>
              <Button 
                onClick={() => navigate(!user ? `/register?redirect=/club/${slug}` : '/membership')}
                style={{ backgroundColor: club.secondary_color }}
                className="text-white hover:opacity-90"
              >
                {!user 
                  ? (currentLanguage === 'es' ? 'Crear Cuenta Gratuita' : 'Create Free Account')
                  : (currentLanguage === 'es' ? 'Ver Planes de Membresía' : 'View Membership Plans')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section - Themed border accent */}
            <section 
              className="bg-white rounded-xl shadow-sm p-6 border-l-4"
              style={{ borderLeftColor: club.primary_color }}
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {currentLanguage === 'es' ? 'Acerca de' : 'About'} {getTranslatedContent('name')}
              </h2>
              <p className="text-gray-700 leading-relaxed">{getTranslatedContent('description')}</p>
              
              <div 
                className="mt-4 p-4 rounded-lg"
                style={{ backgroundColor: `${club.primary_color}08` }}
              >
                <h3 className="font-semibold text-gray-900 mb-2">
                  {currentLanguage === 'es' ? 'Nuestra Misión' : 'Our Mission'}
                </h3>
                <p className="text-gray-600">{getTranslatedContent('mission')}</p>
              </div>
              
              {club.website_url && (
                <a 
                  href={club.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 hover:opacity-80"
                  style={{ color: club.primary_color }}
                >
                  <Globe className="w-4 h-4" />
                  {currentLanguage === 'es' ? 'Visitar Sitio Web Oficial' : 'Visit Official Website'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </section>

            {/* Club Announcements Section */}
            {announcements.length > 0 && (
              <section 
                className="bg-white rounded-xl shadow-sm p-6 border-l-4"
                style={{ borderLeftColor: club.secondary_color }}
                data-testid="club-announcements-section"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Megaphone className="w-5 h-5" style={{ color: club.secondary_color }} />
                  {currentLanguage === 'es' ? 'Anuncios del Club' : 'Club Announcements'}
                </h2>
                <div className="space-y-4">
                  {announcements.map((announcement) => (
                    <div 
                      key={announcement.id}
                      className={`p-4 rounded-lg border ${announcement.is_pinned ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}
                      data-testid={`announcement-${announcement.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {announcement.is_pinned && (
                          <Pin className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            {announcement.title}
                            {announcement.priority === 'high' && (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                                {currentLanguage === 'es' ? 'Importante' : 'Important'}
                              </span>
                            )}
                          </h3>
                          <div className="text-gray-700 mt-2 whitespace-pre-line text-sm">
                            {announcement.content.length > 400 
                              ? `${announcement.content.slice(0, 400)}...` 
                              : announcement.content}
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                            {announcement.author_display_name && (
                              <span>— {announcement.author_display_name}</span>
                            )}
                            {announcement.created_at && (
                              <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Quick Links - Themed hover states */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {currentLanguage === 'es' ? 'Explorar' : 'Explore'} {getTranslatedContent('name')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Link 
                  to="/forums"
                  className="flex flex-col items-center p-4 rounded-xl transition group"
                  style={{ backgroundColor: `${club.primary_color}08` }}
                >
                  <MessageSquare className="w-8 h-8 mb-2" style={{ color: club.primary_color }} />
                  <span className="font-medium text-gray-900">{currentLanguage === 'es' ? 'Foros' : 'Forums'}</span>
                </Link>
                <Link 
                  to="/events"
                  className="flex flex-col items-center p-4 rounded-xl transition"
                  style={{ backgroundColor: `${club.secondary_color}15` }}
                >
                  <Calendar className="w-8 h-8 mb-2" style={{ color: club.secondary_color }} />
                  <span className="font-medium text-gray-900">{currentLanguage === 'es' ? 'Eventos' : 'Events'}</span>
                </Link>
                <Link 
                  to="/marketplace"
                  className="flex flex-col items-center p-4 rounded-xl transition"
                  style={{ backgroundColor: `${club.primary_color}08` }}
                >
                  <ShoppingBag className="w-8 h-8 mb-2" style={{ color: club.primary_color }} />
                  <span className="font-medium text-gray-900">{currentLanguage === 'es' ? 'Mercado' : 'Marketplace'}</span>
                </Link>
                <Link 
                  to="/catalog"
                  className="flex flex-col items-center p-4 rounded-xl transition"
                  style={{ backgroundColor: `${club.secondary_color}15` }}
                >
                  <Dog className="w-8 h-8 mb-2" style={{ color: club.secondary_color }} />
                  <span className="font-medium text-gray-900">{currentLanguage === 'es' ? 'Catálogo' : 'Catalog'}</span>
                </Link>
                <Link 
                  to="/blog"
                  className="flex flex-col items-center p-4 rounded-xl transition"
                  style={{ backgroundColor: `${club.primary_color}08` }}
                >
                  <BookOpen className="w-8 h-8 mb-2" style={{ color: club.primary_color }} />
                  <span className="font-medium text-gray-900">Blog</span>
                </Link>
                {isClubMember && (
                  <Link 
                    to="/club-resources"
                    className="flex flex-col items-center p-4 rounded-xl transition"
                    style={{ backgroundColor: `${club.secondary_color}15` }}
                  >
                    <Award className="w-8 h-8 mb-2" style={{ color: club.secondary_color }} />
                    <span className="font-medium text-gray-900">{currentLanguage === 'es' ? 'Recursos' : 'Resources'}</span>
                  </Link>
                )}
              </div>
            </section>

            {/* Recent Discussions */}
            <section className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Recent Discussions</h2>
                <Link to="/forums" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  View All <ChevronRight className="w-4 h-4 inline" />
                </Link>
              </div>
              
              {recentPosts.length > 0 ? (
                <div className="space-y-3">
                  {recentPosts.map(post => (
                    <Link 
                      key={post.id}
                      to={`/forums/${post.id}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                    >
                      <h3 className="font-medium text-gray-900 line-clamp-1">{post.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {post.author_username} · {post.replies_count || 0} replies
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-6">No recent discussions</p>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Join Card for Non-Members - Themed */}
            {!isClubMember && (
              <div 
                className="rounded-xl shadow-sm p-6 border-t-4"
                style={{ 
                  backgroundColor: 'white',
                  borderTopColor: club.secondary_color 
                }}
              >
                <h3 className="font-bold text-gray-900 mb-3">
                  {currentLanguage === 'es' ? 'Hazte Miembro' : 'Become a Member'}
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <Star className="w-4 h-4" style={{ color: club.secondary_color }} />
                    {currentLanguage === 'es' ? 'Accede a recursos exclusivos' : 'Access exclusive resources'}
                  </li>
                  <li className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" style={{ color: club.primary_color }} />
                    {currentLanguage === 'es' ? 'Únete a discusiones de miembros' : 'Join member-only discussions'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: club.secondary_color }} />
                    {currentLanguage === 'es' ? 'Descuentos en eventos' : 'Discounts on events'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4" style={{ color: club.primary_color }} />
                    {currentLanguage === 'es' ? 'Experiencia sin anuncios' : 'Ad-free experience'}
                  </li>
                </ul>
                <Button 
                  className="w-full text-white hover:opacity-90"
                  style={{ backgroundColor: club.secondary_color }}
                  onClick={() => navigate(!user ? `/register?redirect=/club/${slug}` : '/membership')}
                >
                  {!user 
                    ? (currentLanguage === 'es' ? 'Regístrate Ahora' : 'Sign Up Now')
                    : (currentLanguage === 'es' ? 'Mejorar Membresía' : 'Upgrade Membership')}
                </Button>
              </div>
            )}

            {/* Upcoming Events - Themed */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">
                  {currentLanguage === 'es' ? 'Próximos Eventos' : 'Upcoming Events'}
                </h3>
                <Link 
                  to="/events" 
                  className="text-sm hover:opacity-80"
                  style={{ color: club.primary_color }}
                >
                  {currentLanguage === 'es' ? 'Ver Todos' : 'View All'}
                </Link>
              </div>
              
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.map(event => (
                    <Link 
                      key={event.id}
                      to={`/events/${event.id}`}
                      className="block p-3 rounded-lg transition hover:opacity-80"
                      style={{ backgroundColor: `${club.primary_color}08` }}
                    >
                      <h4 className="font-medium text-gray-900 text-sm line-clamp-1">{event.title}</h4>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(event.start_date).toLocaleDateString()}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4 text-sm">
                  {currentLanguage === 'es' ? 'No hay eventos próximos' : 'No upcoming events'}
                </p>
              )}
            </div>

            {/* Club Stats - Themed */}
            <div 
              className="rounded-xl shadow-sm p-6"
              style={{ 
                backgroundColor: 'white',
                borderBottom: `3px solid ${club.primary_color}`
              }}
            >
              <h3 className="font-bold text-gray-900 mb-4">
                {currentLanguage === 'es' ? 'Estadísticas del Club' : 'Club Stats'}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{currentLanguage === 'es' ? 'Miembros' : 'Members'}</span>
                  <span className="font-semibold" style={{ color: club.primary_color }}>{club.member_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{currentLanguage === 'es' ? 'Tipos de Mascotas' : 'Pet Types'}</span>
                  <span className="font-semibold text-gray-900 capitalize">{club.pet_types?.join(', ')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">{currentLanguage === 'es' ? 'País' : 'Country'}</span>
                  <span className="font-semibold text-gray-900">{club.country}</span>
                </div>
                {club.supported_languages && club.supported_languages.length > 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{currentLanguage === 'es' ? 'Idiomas' : 'Languages'}</span>
                    <span className="font-semibold text-gray-900">
                      {club.supported_languages.map(l => LANGUAGE_LABELS[l]?.flag || l).join(' ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Ad Space for Free Users */}
            {showAds && (
              <div className="bg-gray-100 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-2">Advertisement</p>
                <div className="bg-gray-200 rounded-lg h-48 flex items-center justify-center">
                  <span className="text-gray-400">Ad Space</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <Link to="/membership" className="text-blue-600 hover:underline">
                    Upgrade to remove ads
                  </Link>
                </p>
              </div>
            )}

            {/* Club Admin Section - Officers Only */}
            {isClubOfficer && (
              <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-blue-100">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-gray-900">Club Admin</h3>
                </div>
                <div className="space-y-2">
                  <Link 
                    to={`/club/${slug}/admin/sources`}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                  >
                    <Rss className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Feed Sources</p>
                      <p className="text-xs text-gray-500">Manage curated content</p>
                    </div>
                  </Link>
                  <Link 
                    to={`/club/${slug}/admin/submissions`}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                  >
                    <Settings className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Submission Settings</p>
                      <p className="text-xs text-gray-500">ELO thresholds & blocklist</p>
                    </div>
                  </Link>
                  <Link 
                    to="/blog/admin"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                  >
                    <BookOpen className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Blog Posts</p>
                      <p className="text-xs text-gray-500">Write & manage articles</p>
                    </div>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubHomepage;
